<script setup lang="ts">
// "Не гадалка. Друг, который слушает." — the chat shown as the app's own chat in
// paper (screens/chat/Chat.vue): a raised sheet (the app window) with the talk as
// notes — yours on cream, the cat's on dark stock — and the three drawn cards as
// real deck stickers. Static; below the fold, so the card faces load lazily.
import { Check } from '@lucide/vue';
import catIcon from '@app/assets/images/icons/profile_cat_icon.png';
import ScrollReveal from '@/components/ui/animation/ScrollReveal.vue';

const checklist = [
  'Помнит твою историю и контекст',
  'Отвечает за 10 секунд, 24/7',
  'Честно — даже когда непросто',
];

// Deck keys (public/cards/NN.webp): XVIII The Moon, XVI The Tower, XIX The Sun.
const cards = [
  { name: 'Луна', deck: '18', tilt: -3 },
  { name: 'Башня', deck: '16', tilt: 1.5 },
  { name: 'Солнце', deck: '19', tilt: -1 },
];
const cardFace = (deck: string) => `${import.meta.env.BASE_URL}cards/${deck}.webp`;
</script>

<template>
  <section
    id="chat"
    class="scroll-mt-20 py-16 md:py-24"
  >
    <div class="site-container grid grid-cols-1 items-center gap-10 md:grid-cols-2">
      <ScrollReveal>
        <div class="type-overline mb-4 text-primary-ink">Не предсказание — разговор</div>
        <h2 class="type-display">Не гадалка.<br />Друг, который слушает.</h2>
        <p class="mb-7 mt-5 max-w-[440px] text-[17px] leading-relaxed text-muted-foreground">
          Кот отвечает тепло и по-человечески, объясняет без жаргона и никогда не пугает мрачными предсказаниями. К нему возвращаются месяцами — не за судьбой, а за тем, чтобы тебя выслушали.
        </p>
        <ul class="flex list-none flex-col gap-3 p-0">
          <li
            v-for="(t, i) in checklist"
            :key="i"
            class="flex items-center gap-3 text-[15.5px] text-foreground"
          >
            <span
              class="chat-tick flex h-6 w-6 flex-shrink-0 items-center justify-center"
              aria-hidden="true"
            ><Check
              class="h-3.5 w-3.5"
              :stroke-width="2.5"
            /></span>{{ t }}
          </li>
        </ul>
      </ScrollReveal>

      <ScrollReveal
        :delay="80"
        class="flex justify-center"
      >
        <!-- The app window: a raised sheet of stock, no device frame. -->
        <div class="chat-window paper-sheet-raised flex w-full max-w-[360px] flex-col overflow-hidden">
          <!-- Header -->
          <div class="flex items-center gap-3 px-[18px] pb-3 pt-4">
            <span
              class="chat-avatar paper-tag grid h-[42px] w-[42px] shrink-0 place-items-center"
              role="img"
              aria-label="Таро Кот"
            >
              <img
                :src="catIcon"
                alt=""
                class="h-7 w-7 object-contain brightness-0 opacity-80"
              />
            </span>
            <div>
              <div class="font-serif text-[17px] text-foreground">Таро Кот</div>
              <div class="flex items-center gap-[5px] text-[11.5px] text-success">
                <span class="h-[6px] w-[6px] rounded-full bg-success" />онлайн · мурчит
              </div>
            </div>
          </div>
          <div
            class="chat-rule mx-4"
            aria-hidden="true"
          />

          <!-- Messages -->
          <div class="flex flex-1 flex-col gap-3 p-4">
            <div class="chat-note is-user paper-tag max-w-[80%] self-end px-[14px] py-[11px] text-sm leading-[1.45]">
              Он не отвечает со вчера… он меня разлюбил?
            </div>
            <div class="chat-note paper-sheet max-w-[84%] self-start px-[14px] py-[11px] text-sm leading-normal text-foreground">
              Давай посмотрим, что говорят карты. Вытяну три — про него, про тебя и про то, что дальше 🐾
            </div>
            <ul class="my-1 flex list-none gap-3 self-start p-0 pl-1">
              <li
                v-for="c in cards"
                :key="c.deck"
                class="chat-sticker flex w-[58px] flex-col items-center gap-1.5"
                :style="{ '--tilt': `${c.tilt}deg` }"
              >
                <img
                  :src="cardFace(c.deck)"
                  alt=""
                  width="58"
                  height="102"
                  loading="lazy"
                  decoding="async"
                  class="chat-card h-[102px] w-[58px] object-cover"
                />
                <span class="text-[10.5px] font-medium text-muted-foreground">{{ c.name }}</span>
              </li>
            </ul>
            <div class="chat-note paper-sheet max-w-[84%] self-start px-[14px] py-[11px] text-sm leading-normal text-foreground">
              Он сейчас в своих тревогах — это не про тебя. Солнце впереди говорит: тепло вернётся. Но решать, ждать ли, — только тебе.
            </div>
          </div>

          <!-- Composer: a recessed well on the sheet's foot. -->
          <div class="flex items-center gap-2 px-3 pb-3 pt-1">
            <div class="chat-well flex-1 px-4 py-[10px] text-[13.5px] text-muted-foreground">
              Поделись, что на душе…
            </div>
            <div
              class="chat-send flex h-10 w-10 items-center justify-center bg-primary text-primary-foreground"
              aria-hidden="true"
            >
              ↑
            </div>
          </div>
        </div>
      </ScrollReveal>
    </div>
  </section>
</template>

<style scoped>
.chat-rule {
  border-top: 1.5px dashed color-mix(in srgb, var(--foreground) 16%, transparent);
}
.chat-avatar {
  border-radius: 52% 48% 50% 46% / 48% 52% 46% 50%;
  box-shadow: 0 1px 0 rgb(4 3 18 / 0.5), 0 3px 6px -3px var(--paper-shade);
}
/* Notes on paper: the cat's on dark stock, yours on cream, a hair askew (as the chat). */
.chat-note {
  border-radius: var(--paper-radius-sm);
}
.chat-note.is-user {
  transform: rotate(-0.6deg);
  box-shadow: inset 0 1px 0 rgb(255 255 255 / 0.55), 0 4px 8px -4px var(--paper-shade);
}
.chat-sticker {
  transform: rotate(var(--tilt));
}
/* A deck card as a sticker: cream die-cut margin + a soft lift (as TarotCard). */
.chat-card {
  border-radius: 4px;
  box-shadow:
    0 0 0 3px var(--paper-stock),
    0 8px 12px -4px var(--paper-shade);
}
.chat-tick {
  border-radius: 52% 48% 50% 46% / 48% 52% 46% 50%;
  background: var(--paper-stock);
  color: var(--paper-stock-ink);
  box-shadow: inset 0 1px 0 rgb(255 255 255 / 0.5), 0 2px 4px -1px var(--paper-shade);
}
.chat-well {
  border-radius: var(--paper-radius-sm);
  border: 1px solid color-mix(in srgb, var(--foreground) 40%, transparent);
  background: var(--muted);
  box-shadow: inset 0 2px 4px var(--paper-shade);
}
.chat-send {
  border-radius: var(--paper-radius-sm);
  box-shadow: var(--paper-lift);
}
</style>
